import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Bot,
  Target,
  Settings,
  TrendingUp,
  Zap,
  ShoppingCart,
  Lock,
  X,
  Database,
  ChevronDown,
} from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import UpgradeModal from "./UpgradeModal";

/**
 * Sidebar Navigation Component
 * Shows main navigation menu for authenticated users
 * Responsive: Hidden on mobile with drawer, always visible on desktop
 */
const Sidebar = ({ isOpen, onClose }) => {
  const { canAccessFeature, isFreePlan } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set(["data-sources"]));

  const navItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
      description: "Overview and metrics",
      feature: "dashboard",
      freeAccess: false,
    },
    {
      name: "Data Sources",
      id: "data-sources",
      icon: Database,
      description: "Manage your data",
      feature: "data_sources",
      freeAccess: false,
      isParent: true,
      children: [
        {
          name: "Supported Sources",
          path: "/connectors",
          description: "Connect platforms",
        },
        {
          name: "Connections",
          path: "/connections",
          description: "Manage data syncs",
        },
      ],
    },
    {
      name: "AI Marketing",
      path: "/ai-agent",
      icon: Bot,
      description: "AI strategy generator",
      feature: "ai_agent",
      freeAccess: false,
    },
    {
      name: "CRO Analyzer",
      path: "/cro-analyzer",
      icon: ShoppingCart,
      description: "Conversion optimization",
      feature: "cro_analyzer",
      freeAccess: true,
    },
    {
      name: "Goals",
      path: "/goals",
      icon: Target,
      description: "Track your objectives",
      feature: "goals",
      freeAccess: false,
    },
    {
      name: "Settings",
      path: "/settings",
      icon: Settings,
      description: "Account preferences",
      feature: "settings",
      freeAccess: true,
    },
  ];

  const handleNavClick = (e, item) => {
    if (!item.freeAccess && !canAccessFeature(item.feature)) {
      e.preventDefault();
      setShowUpgradeModal(true);
    }
    // Close mobile menu on navigation
    if (onClose) {
      onClose();
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="route_access"
      />

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-full w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile close button */}
          <div className="lg:hidden flex justify-end p-4 border-b border-gray-200">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isLocked = !item.freeAccess && isFreePlan();
                const hasAccess =
                  item.freeAccess || canAccessFeature(item.feature);

                // Parent item with children
                if (item.isParent && item.children) {
                  const isExpanded = expandedSections.has(item.id);

                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => toggleSection(item.id)}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isLocked
                          ? "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                          }`}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        <div className="flex-1 text-left">
                          <div className="flex items-center justify-between">
                            <span>{item.name}</span>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${isExpanded ? "transform rotate-180" : ""
                                }`}
                            />
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        </div>
                        {isLocked && (
                          <Lock className="w-3 h-3 text-gray-400 ml-1" />
                        )}
                      </button>

                      {/* Child items */}
                      {isExpanded && (
                        <div className="ml-8 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={(e) => handleNavClick(e, item)}
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${isActive && hasAccess
                                  ? "bg-primary-50 text-primary-700 font-medium"
                                  : isLocked
                                    ? "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                }`
                              }
                            >
                              <div className="flex-1">
                                <div>{child.name}</div>
                                <div className="text-xs text-gray-500">
                                  {child.description}
                                </div>
                              </div>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // Regular nav item
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={(e) => handleNavClick(e, item)}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${isActive && hasAccess
                        ? "bg-primary-50 text-primary-700 border-r-2 border-primary-700"
                        : isLocked
                          ? "text-gray-400 hover:text-gray-500 hover:bg-gray-50 cursor-pointer"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      }`
                    }
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span>{item.name}</span>
                        {isLocked && (
                          <Lock className="w-3 h-3 text-gray-400 ml-1" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.description}
                      </div>
                    </div>
                  </NavLink>
                );
              })}
            </div>

            {/* Quick stats section - Hidden on mobile to save space */}
            <div className="mt-8 p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg hidden md:block">
              <div className="flex items-center mb-2">
                <TrendingUp className="w-4 h-4 text-primary-600 mr-2" />
                <span className="text-sm font-medium text-primary-800">
                  Quick Stats
                </span>
              </div>
              <div className="space-y-1 text-xs text-primary-700">
                <div className="flex justify-between">
                  <span>Active Sources:</span>
                  <span className="font-medium">3</span>
                </div>
                <div className="flex justify-between">
                  <span>Goals Tracked:</span>
                  <span className="font-medium">5</span>
                </div>
                <div className="flex justify-between">
                  <span>Last Sync:</span>
                  <span className="font-medium">2h ago</span>
                </div>
              </div>
            </div>

            {/* AI tip section - Hidden on mobile to save space */}
            <div className="mt-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg hidden md:block">
              <div className="flex items-center mb-2">
                <Zap className="w-4 h-4 text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-800">
                  AI Tip
                </span>
              </div>
              <p className="text-xs text-yellow-700">
                Your Google Ads performance is up 15% this week. Consider
                increasing budget for top-performing campaigns.
              </p>
              <button className="mt-2 text-xs text-yellow-800 underline hover:text-yellow-900">
                Generate Strategy →
              </button>
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              CRO Agent v1.0.0
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
